import {
  BaseError,
  ContractFunctionRevertedError,
  createWalletClient,
  custom,
  erc20Abi,
  http,
  numberToHex,
  UserRejectedRequestError,
  type Address,
  type EIP1193Provider,
  type Hash,
  type WalletClient,
} from 'viem'
import { privateKeyToAccount } from 'viem/accounts'
import { flatboardAbi } from './abi/flatboard'
import { publicClient, wifAllowance } from './chain'
import { chain, FLATBOARD, WIF } from './config'
import { wif } from './format'

export interface WalletOption {
  id: string
  name: string
  icon: string | null
  connect: () => Promise<Connected>
  /** Reconnect without a prompt if this site is already authorized, else null. */
  resume: () => Promise<Connected | null>
}

export interface Connected {
  account: Address
  client: WalletClient
  /** Make sure the wallet is on Robinhood Chain before a write. */
  prepare: () => Promise<void>
  /** Called with the new account, or null when the wallet disconnects. */
  onChange: (cb: (account: Address | null) => void) => void
}

interface Eip6963Detail {
  info: { uuid: string; name: string; icon: string; rdns: string }
  provider: EIP1193Provider
}

const discovered = new Map<string, Eip6963Detail>()
window.addEventListener('eip6963:announceProvider', (e) => {
  const detail = (e as CustomEvent<Eip6963Detail>).detail
  discovered.set(detail.info.rdns || detail.info.uuid, detail)
})
window.dispatchEvent(new Event('eip6963:requestProvider'))

/** Every wallet the browser offers, plus the dev wallet when running locally. */
export function walletOptions(): WalletOption[] {
  const options: WalletOption[] = [...discovered.values()].map((d) => ({
    id: d.info.rdns || d.info.uuid,
    name: d.info.name,
    icon: d.info.icon,
    connect: () => connectInjected(d.provider, false),
    resume: () => connectInjected(d.provider, true),
  }))
  const legacy = (window as { ethereum?: EIP1193Provider }).ethereum
  if (options.length === 0 && legacy) {
    options.push({ id: 'injected', name: 'Browser wallet', icon: null, connect: () => connectInjected(legacy, false), resume: () => connectInjected(legacy, true) })
  }
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_PRIVATE_KEY) {
    options.push({ id: 'dev', name: 'Dev wallet (local fork)', icon: null, connect: connectDev, resume: connectDev })
  }
  return options
}

async function connectInjected(provider: EIP1193Provider, silent: true): Promise<Connected | null>
async function connectInjected(provider: EIP1193Provider, silent: false): Promise<Connected>
async function connectInjected(provider: EIP1193Provider, silent: boolean): Promise<Connected | null> {
  const [account] = (await provider.request({ method: silent ? 'eth_accounts' : 'eth_requestAccounts' })) as Address[]
  if (!account) {
    if (silent) return null
    throw new Error('No account was shared by the wallet.')
  }
  if (!silent) await ensureChain(provider)
  return {
    account,
    client: createWalletClient({ account, chain, transport: custom(provider) }),
    prepare: () => ensureChain(provider),
    onChange: (cb) => {
      provider.on?.('accountsChanged', (accounts: readonly Address[]) => cb(accounts[0] ?? null))
    },
  }
}

async function ensureChain(provider: EIP1193Provider) {
  const current = Number(await provider.request({ method: 'eth_chainId' }))
  if (current === chain.id) return
  try {
    await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: numberToHex(chain.id) }] })
  } catch (err) {
    if ((err as { code?: number }).code !== 4902) throw err
    await provider.request({
      method: 'wallet_addEthereumChain',
      params: [
        {
          chainId: numberToHex(chain.id),
          chainName: chain.name,
          nativeCurrency: chain.nativeCurrency,
          rpcUrls: [...chain.rpcUrls.default.http],
          blockExplorerUrls: [chain.blockExplorers.default.url],
        },
      ],
    })
  }
}

async function connectDev(): Promise<Connected> {
  const account = privateKeyToAccount(import.meta.env.VITE_DEV_PRIVATE_KEY as Hash)
  return {
    account: account.address,
    client: createWalletClient({ account, chain, transport: http(chain.rpcUrls.default.http[0]) }),
    prepare: async () => {},
    onChange: () => {},
  }
}

// -----------------------------------------------------------------------------------------
// Writes
// -----------------------------------------------------------------------------------------

export type Step =
  | { kind: 'approve-sign'; amount: bigint }
  | { kind: 'approve-wait'; hash: Hash }
  | { kind: 'burn-sign'; amount: bigint }
  | { kind: 'burn-wait'; hash: Hash }

/** Approve exactly what is needed, unless the existing allowance already covers it. */
async function approveIfNeeded(w: Connected, amount: bigint, onStep: (s: Step) => void) {
  const allowance = await wifAllowance(w.account)
  if (allowance >= amount) return
  onStep({ kind: 'approve-sign', amount })
  const hash = await w.client.writeContract({
    account: w.account,
    chain,
    address: WIF,
    abi: erc20Abi,
    functionName: 'approve',
    args: [FLATBOARD as Address, amount],
  })
  onStep({ kind: 'approve-wait', hash })
  const receipt = await publicClient.waitForTransactionReceipt({ hash })
  if (receipt.status !== 'success') throw new Error('The approval failed onchain.')
}

export async function listProject(
  w: Connected,
  input: { name: string; url: string; tagline: string; token: Address | null; amount: bigint },
  onStep: (s: Step) => void,
): Promise<{ hash: Hash; id: number }> {
  await w.prepare()
  await approveIfNeeded(w, input.amount, onStep)
  const args = [input.name, input.url, input.tagline, input.token ?? '0x0000000000000000000000000000000000000000', input.amount] as const
  const { request, result } = await publicClient.simulateContract({
    account: w.account,
    address: FLATBOARD as Address,
    abi: flatboardAbi,
    functionName: 'list',
    args,
  })
  onStep({ kind: 'burn-sign', amount: input.amount })
  const hash = await w.client.writeContract({ ...request, chain })
  onStep({ kind: 'burn-wait', hash })
  const receipt = await publicClient.waitForTransactionReceipt({ hash })
  if (receipt.status !== 'success') throw new Error('The listing failed onchain.')
  return { hash, id: Number(result) }
}

export async function boostListing(w: Connected, id: number, amount: bigint, onStep: (s: Step) => void) {
  await w.prepare()
  await approveIfNeeded(w, amount, onStep)
  const { request } = await publicClient.simulateContract({
    account: w.account,
    address: FLATBOARD as Address,
    abi: flatboardAbi,
    functionName: 'boost',
    args: [BigInt(id), amount],
  })
  onStep({ kind: 'burn-sign', amount })
  const hash = await w.client.writeContract({ ...request, chain })
  onStep({ kind: 'burn-wait', hash })
  const receipt = await publicClient.waitForTransactionReceipt({ hash })
  if (receipt.status !== 'success') throw new Error('The boost failed onchain.')
  return hash
}

export async function updateTagline(w: Connected, id: number, tagline: string) {
  await w.prepare()
  const { request } = await publicClient.simulateContract({
    account: w.account,
    address: FLATBOARD as Address,
    abi: flatboardAbi,
    functionName: 'setTagline',
    args: [BigInt(id), tagline],
  })
  const hash = await w.client.writeContract({ ...request, chain })
  const receipt = await publicClient.waitForTransactionReceipt({ hash })
  if (receipt.status !== 'success') throw new Error('The tagline update failed onchain.')
  return hash
}

/** Turn whatever a wallet or the chain threw into one sentence that says what to do. */
export function explain(err: unknown): string {
  if (err instanceof BaseError) {
    if (err.walk((e) => e instanceof UserRejectedRequestError)) return 'You cancelled in your wallet. Nothing was burned.'
    const revert = err.walk((e) => e instanceof ContractFunctionRevertedError)
    if (revert instanceof ContractFunctionRevertedError) {
      const name = revert.data?.errorName
      const args = revert.data?.args ?? []
      switch (name) {
        case 'BurnTooSmall':
          return `The minimum here is ${wif(args[1] as bigint)} WIF.`
        case 'ERC20InsufficientBalance':
          return `Your wallet has ${wif(args[1] as bigint)} WIF, which isn't enough for this burn.`
        case 'ERC20InsufficientAllowance':
          return 'The approval did not cover this amount. Try again and approve the full amount.'
        case 'InvalidName':
          return 'The name needs 1 to 32 characters and no line breaks.'
        case 'InvalidUrl':
          return 'The link has to start with https:// and contain no spaces or quotes.'
        case 'InvalidTagline':
          return 'The tagline can be up to 96 characters, on one line.'
        case 'NotLister':
          return 'Only the wallet that listed this project can change its tagline.'
        case 'UnknownListing':
          return 'That listing does not exist.'
      }
    }
    if (/insufficient funds/i.test(err.message)) return 'Your wallet needs a little ETH on Robinhood Chain to pay gas.'
    return err.shortMessage
  }
  if ((err as { code?: number })?.code === 4001) return 'You cancelled in your wallet. Nothing was burned.'
  return err instanceof Error ? err.message : 'Something went wrong. Check your wallet and try again.'
}
